import { useEffect, useRef, useState } from 'react';
import { DBRequest, useConfig } from '../../api/api';
import {
  Box,
  IconButton,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  useTheme,
} from '@mui/material';
import { GantDay } from './gant/GantDay';
import { getTimelineDays } from './gant/getTimelineDays';
import { GantPhase } from './gant/GantPhase';
import { updateOrCreate } from '../special/updateOrCreate';
import { ProjectAddElementButton } from './project/ProjectAddElementButton';
import { tokens } from '../../global/theme/tokens';
import { ExoSkeleton } from './ExoSkeleton';
import FullscreenIcon from '@mui/icons-material/Fullscreen';
import FullscreenExitIcon from '@mui/icons-material/FullscreenExit';
import DirectionsRunIcon from '@mui/icons-material/DirectionsRun';
import FlagIcon from '@mui/icons-material/Flag';
import AllInclusiveIcon from '@mui/icons-material/AllInclusive';
import { t } from 'i18next';
import ReportGmailerrorredIcon from '@mui/icons-material/ReportGmailerrorred';

export const ExoGant = ({
  projectId,
  inEditMode,
  resolution = 1,
  editable,
}) => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode, theme.palette.colorTheme);
  const config = useConfig();
  const [apiData, setApiData] = useState(null);
  const [days, setDays] = useState(null);
  const [fullScreen, setFullScreen] = useState(false);

  useEffect(() => {
    DBRequest({
      config,
      path: `projects/${projectId}/phases`,
      onResponse: handleResponse,
    });
  }, [config]);

  function handleResponse(res) {
    setApiData(res);
  }

  useEffect(() => {
    if (apiData) setDays(getTimelineDays(apiData.data));
  }, [apiData]);

  function handlePhaseChange(newPhase) {
    setApiData(updateOrCreate(apiData, newPhase));
  }

  // responsive
  const containerRef = useRef(null);
  const dayRef = useRef(null);
  const [dayWidth, setDayWidth] = useState(80); // default

  function handleResize(width) {
    setDayWidth(width);
  }

  useEffect(() => {
    const container = dayRef.current;
    if (!container) return;

    const resizeObserver = new ResizeObserver((entries) => {
      for (let entry of entries) {
        const width = entry.contentRect.width;
        handleResize(width);
      }
    });

    resizeObserver.observe(container);

    return () => {
      resizeObserver.unobserve(container);
      resizeObserver.disconnect();
    };
  }, [dayRef, dayRef.current, days]);

  // hide wps with a certaint status
  const [allowedStatuses, setAllowedStatuses] = useState([0, 2]);

  if (!days || !apiData)
    return (
      <Box
        className="backdrop-blur-md rounded-lg flex flex-col h-full overflow-hidden"
        sx={{ bgcolor: colors.glass }}
      >
        <ExoSkeleton n={1} size="56px" borderRadius="0px" />
        <Box className="py-2 flex flex-col px-1 gap-1">
          <Box className="pb-2">
            <ExoSkeleton n={1} size="23px" width="180px" />
          </Box>
          <Box className=" pl-[0%] pr-[75%]">
            <ExoSkeleton n={1} size="50px" />
          </Box>
          <Box className=" pl-[20%] pr-[60%]">
            <ExoSkeleton n={1} size="50px" />
          </Box>
          <Box className=" pl-[40%] pr-[20%]">
            <ExoSkeleton n={1} size="50px" />
          </Box>
          <Box className=" pl-[80%] pr-[0%]">
            <ExoSkeleton n={1} size="50px" />
          </Box>
        </Box>
        <Box className="py-2 flex flex-col px-1 gap-1 pt-2">
          <Box className="pb-2">
            <ExoSkeleton n={1} size="23px" width="180px" />
          </Box>
          <Box className=" pl-[0%] pr-[35%]">
            <ExoSkeleton n={1} size="50px" />
          </Box>
          <Box className=" pl-[60%] pr-[10%]">
            <ExoSkeleton n={1} size="50px" />
          </Box>
        </Box>
      </Box>
    );

  return (
    <Box
      className="backdrop-blur-md rounded-lg overflow-hidden flex flex-col h-full top-0 left-0 w-full z-[51]"
      sx={{
        position: fullScreen ? 'fixed' : 'relative',
        transition: '0.8s ease',
      }}
    >
      <Box
        ref={containerRef}
        className="grid grid-flow-col overflow-x-auto overflow-y-hidden h-full relative"
        sx={{
          gridAutoColumns: 'minmax(80px, 1fr)',
        }}
      >
        {days?.map(
          (day, index) =>
            index % resolution === 0 && (
              <GantDay
                key={day.date.format('YYYY-MM-DD')}
                dayRef={index === 0 ? dayRef : null}
                day={day}
                isFirst={index === 0}
                isLast={index === days.length - 1}
              />
            )
        )}
        <Box
          className="h-full absolute bottom-1 left-0 pt-[65px] z-[5] overflow-x-hidden overflow-y-auto pb-[45px]"
          sx={{
            width: Boolean(dayWidth && days?.length)
              ? (dayWidth * days?.length) / resolution
              : 0,
          }}
        >
          {apiData?.data.map((phase, index) => (
            <GantPhase
              key={phase.id}
              phase={phase}
              isFirst={index === 0}
              dayWidth={dayWidth}
              days={days}
              onChange={handlePhaseChange}
              inEditMode={inEditMode}
              projectId={projectId}
              resolution={resolution}
              allowedStatuses={allowedStatuses}
              editable={editable}
            />
          ))}
        </Box>
      </Box>
      <ToggleButtonGroup
        size="small"
        value={allowedStatuses}
        onChange={(e, value) => setAllowedStatuses(value)}
        aria-label="WP Status"
        className="backdrop-blur-lg absolute bottom-2 left-1 z-10"
      >
        <Tooltip title={t('Running')}>
          <ToggleButton value={0}>
            <DirectionsRunIcon />
          </ToggleButton>
        </Tooltip>
        <Tooltip title={t('Infinite')}>
          <ToggleButton value={2}>
            <AllInclusiveIcon />
          </ToggleButton>
        </Tooltip>
        <Tooltip title={t('Finished')}>
          <ToggleButton value={1}>
            <FlagIcon />
          </ToggleButton>
        </Tooltip>
        <Tooltip title={t('Canceled')}>
          <ToggleButton value={3}>
            <ReportGmailerrorredIcon />
          </ToggleButton>
        </Tooltip>
      </ToggleButtonGroup>
      <IconButton
        onClick={() => setFullScreen(!fullScreen)}
        sx={{ position: 'absolute', bgcolor: colors.card }}
        className="bottom-2 right-1 z-10 backdrop-blur-md"
      >
        {fullScreen ? <FullscreenExitIcon /> : <FullscreenIcon />}
      </IconButton>
    </Box>
  );
};
